import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiPut } from '@/lib/api';
import { pricebookKeys, invoiceKeys } from '@/lib/queryKeys';
import { useApiMutation } from '../useApiMutation';
import { usePricebookApi } from './usePricebookApi';
import type { UpdateItemDto } from '@/types/pricebook';
import { ITEM_TYPE_TO_BACKEND } from '@/types/pricebook';

export function useUpdateItem() {
  const queryClient = useQueryClient();
  const { client, ep } = usePricebookApi();

  return useApiMutation<unknown, UpdateItemDto>({
    mutationFn: ({ id, ...rest }) => {
      const payload = {
        ...rest,
        ...(rest.type ? { type: ITEM_TYPE_TO_BACKEND[rest.type] ?? rest.type } : {}),
      };
      return apiPut(client, ep.update(id), payload);
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: pricebookKeys.lists() });
      // Materials/equipment are dependencies of dynamic services. Their price
      // and cost changes must refresh both detail and batch reprice previews so
      // update indicators appear without a page reload.
      void queryClient.invalidateQueries({ queryKey: ['pricebook', 'pricing', 'preview'] });
      void queryClient.invalidateQueries({ queryKey: ['pricebook', 'pricing', 'previews'] });
      if (variables.id) {
        void queryClient.invalidateQueries({ queryKey: pricebookKeys.detail(variables.id) });
      }
      if (variables.basePrice !== undefined) {
        void queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
      }
      toast.success('Item updated');
    },
  });
}
